/**
 * Building and validating diver suggestions. The payload rules live in the contracts
 * (`SuggestionCreate`); this module only turns the admin form models into payloads:
 * - no `status`, no `slug`, never a measured depth (moderators set those),
 * - edits carry ONLY the top-level keys that changed, each one complete (every locale of an
 *   i18n text, all six ratings, the whole closed-periods list): the server replaces whole keys,
 * - an edit needs a note.
 */
import type { DiveObject, SpotDetail } from "@divespots/contracts";
import { SuggestionCreate } from "@divespots/contracts";
import {
  TEXT_KEYS,
  formToObjectInput,
  formToSpotInput,
  objectPatch,
  objectToForm,
  spotPatch,
  spotToForm,
  toLocaleTexts,
} from "../admin/forms.ts";
import type { FieldErrors, ObjectForm, SpotForm, Validation } from "../admin/forms.ts";
import { issuesToErrors } from "../admin/forms.ts";

type T = (key: string) => string;
type Body = Record<string, unknown>;

/** What a diver may never send. */
const STAFF_ONLY_SPOT = ["status", "slug", "permanentClosure"] as const;
const STAFF_ONLY_OBJECT = ["status", "depthOverrideM"] as const;

function without<K extends string>(o: Body, keys: readonly K[]): Body {
  const out: Body = { ...o };
  for (const k of keys) delete out[k];
  return out;
}

export const spotCreatePayload = (f: SpotForm): Body =>
  without(formToSpotInput(f), STAFF_ONLY_SPOT);

export const objectCreatePayload = (spotId: string, f: ObjectForm): Body => ({
  spotId,
  ...without(formToObjectInput(f), STAFF_ONLY_OBJECT),
});

/** Changed keys only, without staff-only ones. `{}` = nothing changed. */
export function spotEditPayload(original: SpotDetail, f: SpotForm): Body {
  return without(spotPatch(original, { ...f, slug: "", status: original.status }), STAFF_ONLY_SPOT);
}

export function objectEditPayload(original: DiveObject, f: ObjectForm): Body {
  return without(
    objectPatch(original, {
      ...f,
      status: original.status,
      override: objectToForm(original).override,
    }),
    STAFF_ONLY_OBJECT,
  );
}

/** Field errors keyed like the form fields: a schema path `payload.name` becomes `name`. */
function toErrors(
  issues: readonly { path: readonly PropertyKey[]; message: string; code?: string }[],
  t: T,
): FieldErrors {
  return issuesToErrors(
    issues.map((i) => ({ ...i, path: i.path[0] === "payload" ? i.path.slice(1) : i.path })),
    t,
  );
}

function validate(body: Body, t: T): Validation<SuggestionCreate> {
  const parsed = SuggestionCreate.safeParse(body);
  if (parsed.success) return { ok: true, data: body as SuggestionCreate };
  return { ok: false, errors: toErrors(parsed.error.issues, t) };
}

const noteOf = (note: string) => note.trim();

export function validateSpotSuggestion(f: SpotForm, note: string, t: T) {
  return validate(
    {
      kind: "spot_create",
      payload: spotCreatePayload(f),
      ...(noteOf(note) ? { note: noteOf(note) } : {}),
    },
    t,
  );
}

export function validateObjectSuggestion(spotId: string, f: ObjectForm, note: string, t: T) {
  return validate(
    {
      kind: "object_create",
      payload: objectCreatePayload(spotId, f),
      ...(noteOf(note) ? { note: noteOf(note) } : {}),
    },
    t,
  );
}

/** "nothing changed" and "note missing" are reported on the pseudo field keys `_` and `note`. */
function editBody(
  kind: "spot_edit" | "object_edit",
  targetId: string,
  payload: Body,
  note: string,
  t: T,
) {
  const errors: FieldErrors = {};
  if (Object.keys(payload).length === 0) errors._ = t("suggest.err.nothingChanged");
  if (noteOf(note) === "") errors.note = t("suggest.err.noteRequired");
  if (Object.keys(errors).length > 0) return { ok: false, errors } as const;
  return validate({ kind, targetId, payload, note: noteOf(note) }, t);
}

export const validateSpotEditSuggestion = (original: SpotDetail, f: SpotForm, note: string, t: T) =>
  editBody("spot_edit", original.id, spotEditPayload(original, f), note, t);

export const validateObjectEditSuggestion = (
  original: DiveObject,
  f: ObjectForm,
  note: string,
  t: T,
) => editBody("object_edit", original.id, objectEditPayload(original, f), note, t);

// ------------------------------------------------------------------ payload -> form (adjust before approve)

/** Applies a (partial) spot payload on top of a form: the moderator edits what the diver sent. */
export function applySpotPayload(base: SpotForm, p: Body): SpotForm {
  const f: SpotForm = structuredClone(base);
  const loc = p.location as { lat: number; lon: number } | undefined;
  if (loc) {
    f.lat = String(loc.lat);
    f.lon = String(loc.lon);
  }
  if ("parkingLocation" in p) {
    const pl = p.parkingLocation as { lat: number; lon: number } | null;
    f.hasParking = pl !== null;
    f.parkLat = pl ? String(pl.lat) : "";
    f.parkLon = pl ? String(pl.lon) : "";
  }
  if (typeof p.name === "string") f.name = p.name;
  if (typeof p.waterBody === "string") f.waterBody = p.waterBody;
  if (typeof p.locality === "string") f.locality = p.locality;
  if ("difficulty" in p) f.difficulty = (p.difficulty as SpotForm["difficulty"] | null) ?? "";
  if ("maxDepthM" in p) f.maxDepthM = p.maxDepthM === null ? "" : String(p.maxDepthM);
  if (Array.isArray(p.closedPeriods)) {
    f.closedPeriods = (
      p.closedPeriods as { from: string; to: string; reason?: Record<string, string> }[]
    ).map((c) => ({ from: c.from, to: c.to, reason: toLocaleTexts(c.reason) }));
  }
  if (p.ratings) f.ratings = { ...(p.ratings as SpotForm["ratings"]) };
  for (const k of TEXT_KEYS) {
    if (p[k] !== undefined) f.texts[k] = toLocaleTexts(p[k] as Record<string, string>);
  }
  return f;
}

export function applyObjectPayload(base: ObjectForm, p: Body): ObjectForm {
  const f: ObjectForm = structuredClone(base);
  if (typeof p.type === "string") f.type = p.type as ObjectForm["type"];
  if (p.name) f.name = toLocaleTexts(p.name as Record<string, string>);
  if (p.description) f.description = toLocaleTexts(p.description as Record<string, string>);
  const loc = p.location as { lat: number; lon: number } | undefined;
  if (loc) {
    f.lat = String(loc.lat);
    f.lon = String(loc.lon);
  }
  return f;
}

export { spotToForm, objectToForm };
